import { useEffect, useRef } from "react";

import { useTranslation } from "@impero-com/common/utils/i18n";
import { emitNotification } from "@impero-com/ui";
import { Column } from "@tanstack/react-table";
import { z } from "zod";

export function useValidatedTSTFilterValue<TSchema extends z.ZodTypeAny>(
    value: unknown,
    schema: TSchema,
    column: Column<any>
): z.infer<TSchema> | undefined {
    const lastInvalidValue = useRef<unknown>(null);
    const { t } = useTranslation();

    useEffect(() => {
        const validationResult = schema.safeParse(value);

        if (
            !validationResult.success &&
            lastInvalidValue.current !== value &&
            value !== undefined
        ) {
            column.setFilterValue(undefined);
            lastInvalidValue.current = value;
            const head = column.columnDef.header;
            let filterName: string;
            if (typeof head === "string") {
                filterName = head;
            } else if (typeof head === "function") {
                // Context is typically not required
                const headerFn = head as (context?: any) => any;
                filterName = String(headerFn());
            } else {
                filterName = column.id;
            }
            console.debug(
                `Filter validation failed for column ${
                    column.id
                }. Input value: ${
                    value as unknown
                }, Validated value: ${JSON.stringify(validationResult.data)}`
            );
            const errorMsg = t("Table_RemovedFilterWarning", {
                filterName,
            });
            emitNotification("warning", errorMsg, { duration: "DOUBLE" });
        } else if (validationResult.success) {
            lastInvalidValue.current = null;
        }
    }, [value, schema, column, t]);

    const validationResult = schema.safeParse(value);
    return validationResult.data;
}
